import {Session} from '@supabase/supabase-js';
import React, {useContext, useEffect, useState} from 'react';
import { Platform } from 'react-native';
import SplashScreen from 'react-native-splash-screen';
import {Supabase} from './supabase.config';
import { getUserData, getUserRole, updateUser } from '../services/userService';

export const AuthContext = React.createContext({});

export const useAuth: any = () => useContext(AuthContext);

const AuthProvider: React.FC<{children: JSX.Element}> = ({children}) => {
  const [appSession, setAppSession] = useState<Session | null>(null);
  const [user, setUser] = useState<any>(null); // Supabase user
  const [userData, setUserData] = useState<any>(null); // Custom user data
  const [loading, setLoading] = useState(true);

  // Handle Supabase OAuth hash fragment on web (e.g. Google sign-in redirect)
  const handleWebOAuthRedirect = async () => {
    if (Platform.OS !== 'web') return;
    if (typeof window === 'undefined') return;

    const hash = window.location.hash || '';
    
    if (!hash.includes('access_token')) return;

    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');

    if (!access_token || !refresh_token) {
      return;
    }

    try {
      const { data, error } = await Supabase.auth.setSession({
        access_token,
        refresh_token,
      });

      if (error) {
        // Only log actual errors, not expected flow issues
        return;
      }

      if (data.session) {
        setAppSession(data.session);
        setUser(data.session.user ?? null);
        if (data.session.user) {
          // Set from_web = true for web Google sign-in
          //if (Platform.OS === 'web') {
            try {
              await updateUser(data.session.user.id, { from_web: true });
            } catch (error) {
              console.warn('Failed to update from_web flag for Google sign-in:', error);
              // Don't block authentication flow
            }
         // }
          await updateUserData(data.session.user);
        }
      }
    } catch (error) {
      // Silently handle OAuth errors - they're expected in some cases
    } finally {
      // Clean URL (remove tokens from hash)
      try {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname + window.location.search
        );
      } catch {
        // Ignore URL cleaning errors
      }
    }
  };

  // Fetch session and user on mount
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        // On web, first handle OAuth redirect hash (e.g. Google sign-in)
        await handleWebOAuthRedirect();

        const { data: { session } } = await Supabase.auth.getSession();
        if (!isMounted) return;

      setAppSession(session);
      setUser(session?.user ?? null);
        if (session?.user) {
          await updateUserData(session.user);
        }
      } catch {
        // Ignore; fall through to finally to stop loading
      } finally {
        if (isMounted) {
      setLoading(false);
      SplashScreen.hide();
        }
      }
    };

    init();

    const { data: listener } = Supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!isMounted) return;
      setAppSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      SplashScreen.hide();
      if (session?.user) {
        // Set from_web = true for web Google sign-in (handles both new and existing users)
        //if (Platform.OS === 'web') {
          try {
            await updateUser(session.user.id, { from_web: true });
          } catch (error) {
            console.warn('Failed to update from_web flag for Google sign-in:', error);
            // Don't block authentication flow
          }
        //}
        updateUserData(session.user);
      } else {
        setUserData(null);
      }
    });

    return () => {
      isMounted = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  // Fetch user data from DB
  const updateUserData = async (user: any) => {
    try {
      const userDataRes = await getUserData(user.id);
      const userRoleRes = await getUserRole(user.id);
      
      if (userDataRes.success && userRoleRes.success) {
        const combinedUserData = {
          ...userDataRes.data,
          role: userRoleRes.data.role,
        };
        setUserData(combinedUserData);
      } else {
        // Failed to fetch user data or role
      }
    } catch (error) {
      // Handle error silently
    }
  };

  const setAuth = (session: Session | null) => {
    setAppSession(session);
    setUser(session?.user ?? null);
    if (session?.user) {
      updateUserData(session.user);
    } else {
      setUserData(null);
    }
  };

  // Expose refresh function so any component can refresh user data
  const refreshUserData = async () => {
    if (user) {
      await updateUserData(user);
    }
  };

  return (
    <AuthContext.Provider value={{ appSession, user, userData, setAuth, setUserData, refreshUserData }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
